import type { HTMLAttributes } from "react";
import React from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import type { Account } from "../../../../../shared/domains/account/account";
import { Path } from "../../../../core/routing/path";
import { DefaultLink } from "../../../common/buttons/default-button";
import { ChevronIcon } from "../../../common/svg/chevron";
import { theme } from "../../../styles/theme";
import { ACCOUNT_QUERY_PARAM } from "../utils/use-selected-account";

interface RequiredActionsCardProps extends HTMLAttributes<HTMLDivElement> {
  account: Account;
  toJustifyCount: number;
  toQualifyCount: number;
}

export function RequiredActionsCard(props: Readonly<RequiredActionsCardProps>) {
  const { account, toJustifyCount, toQualifyCount, ...rest } = props;
  const { formatMessage } = useIntl();
  const allTransactions = `${Path.AllTransactions}?${ACCOUNT_QUERY_PARAM}=${account.id}`;

  if (toJustifyCount <= 0 && toQualifyCount <= 0) {
    return null;
  }

  return (
    <Card {...rest}>
      <Header>
        <Title>{formatMessage("homeScreen.requiredActionsTitle")}</Title>
        <SeeAllLink to={allTransactions}>{formatMessage("homeScreen.requiredActionsSeeAll")}</SeeAllLink>
      </Header>
      {toJustifyCount > 0 && (
        <ActionRow to={allTransactions}>
          <ActionLabel>{formatMessage("homeScreen.transactionsToJustify")}</ActionLabel>
          <CountBadge>{toJustifyCount}</CountBadge>
          <RowChevron />
        </ActionRow>
      )}
      {toQualifyCount > 0 && (
        <ActionRow to={allTransactions}>
          <ActionLabel>{formatMessage("homeScreen.transactionsToQualify")}</ActionLabel>
          <CountBadge>{toQualifyCount}</CountBadge>
          <RowChevron />
        </ActionRow>
      )}
    </Card>
  );
}

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  padding: 16px;
  border-radius: 16px;
  background-color: #ffffff;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  ${theme.mediumText};
  margin: 0;
  font-size: 14px;
  line-height: 18px;
  color: ${theme.colors.gray[800]};
`;

const SeeAllLink = styled(DefaultLink)`
  ${theme.mediumText};
  font-size: 13px;
  color: ${theme.colors.blue[700]};
`;

const ActionRow = styled(DefaultLink)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 14px 16px;
  border: 1px solid ${theme.colors.blue[700]};
  border-radius: 12px;
  color: ${theme.colors.blue[700]};

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const ActionLabel = styled.span`
  ${theme.mediumText};
  flex: 1;
  text-align: left;
  font-size: 14px;
  line-height: 18px;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background-color: ${theme.colors.blue[700]};
  color: #ffffff;
  font-size: 12px;
`;

const RowChevron = styled(ChevronIcon)`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  fill: ${theme.colors.blue[700]};
`;
